import { z } from 'zod';
import { ROLES } from './roles.js';
import { rialInput, quantityInput } from './moneySchema.js';
import { TRANCHE_METHODS, DEFAULT_TRANCHE_METHOD } from './payments.js';
// Money fields use `rialInput` (the ONE integer-rial validator, shared with the GL — moneySchema.ts); a `qty`
// uses `quantityInput` (finite, positive, may be FRACTIONAL — materials are per-kg). The old local `rialAmount`
// helper omitted `.int()`, letting a fractional rial through these schemas while the ledger rejected it (CW-06).
/**
 * Validate a party's (customer/supplier) EMAIL when provided — a malformed email is always a typo, never
 * partial data, so rejecting it at entry is safe and low-friction. The tax identifiers (econ_no / national_id)
 * are deliberately NOT hard-validated here: at the CRM they are free-form (entered partially, searched on),
 * and their validity is enforced where it actually matters — the مودیان invoice (E4-02), which the tax system
 * rejects if the buyer code is malformed. That is the correct enforcement point (using the iranId authority),
 * not free-form customer entry.
 */
function refineParty(v, ctx) {
    if (v.email && !z.string().email().safeParse(v.email).success)
        ctx.addIssue({ code: 'custom', path: ['email'], message: 'ایمیل نامعتبر است' });
}
/**
 * Request validation schemas shared by the server (runtime validation) and the web (form types).
 * One source of truth — the client and server can never disagree on what a valid payload is.
 */
export const LoginSchema = z.object({
    username: z.string().min(1, 'نام کاربری لازم است').max(64),
    password: z.string().min(1, 'گذرواژه لازم است').max(200),
    /** optional second factor — a 6-digit TOTP or a recovery code, supplied on the 2FA step */
    otp: z.string().max(32).optional(),
});
/** A 2FA token: a 6-digit TOTP code or a recovery code (used to enable/disable). */
export const TotpTokenSchema = z.object({ token: z.string().min(1, 'کد لازم است').max(32) });
export const CustomerSchema = z
    .object({
    id: z.number().int().positive().optional(),
    version: z.number().int().positive().optional(),
    code: z.string().max(64).optional(),
    name: z.string().min(1, 'نام مشتری لازم است').max(200),
    econ_no: z.string().max(64).optional(),
    national_id: z.string().max(64).optional(),
    phone: z.string().max(64).optional(),
    email: z.string().max(120).optional(),
    address: z.string().max(500).optional(),
    credit_limit: rialInput('سقف اعتبار نامعتبر است', { positive: false }).optional(),
    terms: z.string().max(200).optional(),
    notes: z.string().max(2000).optional(),
})
    .superRefine(refineParty);
export const SupplierSchema = z
    .object({
    id: z.number().int().positive().optional(),
    version: z.number().int().positive().optional(),
    code: z.string().max(64).optional(),
    name: z.string().min(1, 'نام تأمین‌کننده لازم است').max(200),
    econ_no: z.string().max(64).optional(),
    national_id: z.string().max(64).optional(),
    phone: z.string().max(64).optional(),
    email: z.string().max(120).optional(),
    address: z.string().max(500).optional(),
    terms: z.string().max(200).optional(),
    notes: z.string().max(2000).optional(),
})
    .superRefine(refineParty);
export const PoItemSchema = z.object({
    material_code: z.string().min(1, 'کد ماده لازم است'),
    qty: quantityInput('مقدار باید بزرگ‌تر از صفر باشد'),
    price: rialInput('قیمت باید بزرگ‌تر از صفر باشد'),
});
export const PoSchema = z.object({
    id: z.number().int().positive().optional(),
    version: z.number().int().positive().optional(),
    supplier_id: z.number().int().positive().nullable().optional(),
    notes: z.string().optional(),
    /** ISO date the goods are expected in — dates the payable before receipt (audit E6) */
    expected_receipt: z.string().optional(),
    items: z.array(PoItemSchema).min(1, 'حداقل یک قلم لازم است'),
});
export const CompanyProfileSchema = z.object({
    legal_name: z.string().max(200).optional(),
    legal_name_en: z.string().max(200).optional(),
    address: z.string().max(500).optional(),
    postal_code: z.string().max(32).optional(),
    econ_no: z.string().max(64).optional(),
    national_id: z.string().max(64).optional(),
    phone: z.string().max(64).optional(),
    email: z.string().max(120).optional(),
    website: z.string().max(120).optional(),
    bank: z.string().max(200).optional(),
});
export const DocTermsSchema = z.object({
    pay_terms: z.string().max(500).optional(),
    ship_terms: z.string().max(500).optional(),
    penalty: z.string().max(500).optional(),
    day_price_clause: z.string().max(1000).optional(),
    fixed_price_clause: z.string().max(1000).optional(),
    notes: z.string().max(1000).optional(),
    remarks: z.string().max(1000).optional(),
    validity_days: z
        .number()
        .int('تعداد روز باید عدد صحیح باشد')
        .min(0, 'تعداد روز نمی‌تواند منفی باشد')
        .max(365, 'حداکثر ۳۶۵ روز')
        .optional(),
});
export const DealItemSchema = z
    .object({
    product_code: z.string().min(1, 'کد محصول لازم است'),
    qty: quantityInput('تعداد باید بزرگ‌تر از صفر باشد'),
    price: rialInput('قیمت فروش باید بزرگ‌تر از صفر باشد'),
    // WS-C (note #1): absolute rial discount off the line total; display-only unit + description.
    discount: rialInput('تخفیف نمی‌تواند منفی باشد', { positive: false }).optional(),
    unit: z.string().max(20).optional(),
    description: z.string().max(200).optional(),
})
    .refine((it) => (it.discount ?? 0) <= it.qty * it.price, {
    message: 'تخفیف نمی‌تواند از مبلغ خط بیشتر باشد',
    path: ['discount'],
});
export const DealSchema = z.object({
    id: z.number().int().positive().optional(),
    version: z.number().int().nonnegative().optional(),
    customer_id: z.number().int().positive().nullable().optional(),
    validity: z.string().optional(),
    ship_terms: z.string().optional(),
    pay_terms: z.string().optional(),
    penalty: z.string().optional(),
    price_fixed: z.boolean().optional(),
    notes: z.string().optional(),
    remarks: z.string().optional(),
    /** Payment schedule (CIRCULARITY_360 C2): ordered tranches; empty/absent → config default. */
    payment_schedule: z
        .array(z.object({
        pct: z.number().min(0).max(100),
        anchor: z.enum(['order', 'delivery']),
        offset_days: z.number().int().min(0).max(3650),
        label: z.string().max(60).optional().default(''),
        // How the tranche is collected; legacy schedules with no method default to cheque (§4).
        method: z.enum(TRANCHE_METHODS).optional().default(DEFAULT_TRANCHE_METHOD),
    }))
        // A NON-EMPTY schedule must cover the whole contract (Σpct = 100). Without this a deal could be saved with
        // tranches summing to 60% or 140%, silently corrupting the cash-calendar receivable projection. Same
        // Math.round(sum)===100 tolerance the ScheduleEditor indicator and the template manager already use.
        .refine((arr) => arr.length === 0 || Math.round(arr.reduce((s, t) => s + t.pct, 0)) === 100, {
        message: 'مجموع درصدهای زمان‌بندی پرداخت باید ۱۰۰٪ شود',
    })
        .optional(),
    /** Expected delivery date (ISO) — dates delivery-anchored tranches before actual shipment. */
    expected_delivery: z.string().optional(),
    items: z.array(DealItemSchema).min(1, 'حداقل یک قلم با محصول معتبر لازم است'),
});
/** Minimum password length — the one floor used by user creation, self-change, and admin reset. */
export const PASSWORD_MIN = 8;
const passwordField = z.string().min(PASSWORD_MIN, `گذرواژه حداقل ${PASSWORD_MIN} نویسه`).max(200);
export const CreateUserSchema = z.object({
    username: z.string().min(1, 'نام کاربری لازم است').max(64),
    password: passwordField,
    role: z.enum(ROLES),
    name: z.string().min(1, 'نام لازم است').max(120),
});
/** A logged-in user rotating their OWN password (old verified server-side, new meets the floor). */
export const ChangePasswordSchema = z.object({
    oldPassword: z.string().min(1, 'گذرواژهٔ فعلی لازم است').max(200),
    newPassword: passwordField,
});
/** An admin resetting another user's password (no old password — the admin is trusted for this). */
export const ResetPasswordSchema = z.object({ newPassword: passwordField });
/** An admin changing a user's role. */
export const SetRoleSchema = z.object({ role: z.enum(ROLES) });
/** First Zod issue as a `{ field, message }` pair for the API error contract. */
export function firstIssue(error) {
    const issue = error.issues[0];
    return {
        field: issue ? issue.path.join('.') : '',
        message: issue ? issue.message : 'مقدار نامعتبر',
    };
}
/**
 * Public website inquiry (unauthenticated). Hard caps on every field; qty is
 * coerced because plain HTML form posts arrive as strings. The `website`
 * honeypot and rate limiting are enforced in the route, not the schema.
 */
export const PublicInquirySchema = z.object({
    company: z.string().trim().min(1, 'نام شرکت لازم است').max(120),
    contact: z.string().trim().max(80).optional().default(''),
    phone: z.string().trim().min(5, 'شماره تماس لازم است').max(32),
    email: z.string().trim().max(120).optional().default(''),
    market: z.enum(['IR', 'IQ', 'other']).optional().default('IR'),
    product: z.string().trim().min(1, 'نام محصول لازم است').max(160),
    product_code: z
        .string()
        .trim()
        .max(32)
        .regex(/^[0-9]*$/, 'کد محصول نامعتبر است')
        .optional()
        .default(''),
    qty: z.coerce.number().int('تعداد باید عدد صحیح باشد').min(1, 'تعداد لازم است').max(1_000_000),
    delivery: z
        .string()
        .trim()
        .regex(/^(\d{4}-\d{2}-\d{2})?$/, 'تاریخ تحویل نامعتبر است')
        .optional()
        .default(''),
    notes: z.string().trim().max(2000).optional().default(''),
    locale: z.enum(['fa', 'ar', 'en']).optional().default('fa'),
});
export const INQUIRY_STATUSES = ['new', 'read', 'converted', 'archived'];
export const InquiryStatusSchema = z.object({
    status: z.enum(INQUIRY_STATUSES),
    deal_id: z.number().int().positive().optional(),
});
//# sourceMappingURL=schemas.js.map